export interface CrmBookingRuleOverride {
  id: string;
  personId: string;
  serviceId: string | null;
  emergencyPassesRemaining: number | null;
  customLateCancelHours: number | null;
  isVip: boolean;
  notes: string | null;
}

export interface CrmBookingRuleServiceOption {
  id: string;
  name: string;
}

export interface CrmBookingRuleOverrideDraft {
  id: string | null;
  serviceId: string;
  customLateCancelHours: string;
  emergencyPassesRemaining: string;
  isVip: boolean;
  notes: string;
}

interface CrmContactBookingOverridesCardProps {
  overrides: CrmBookingRuleOverride[];
  services: CrmBookingRuleServiceOption[];
  draft: CrmBookingRuleOverrideDraft;
  error: string | null;
  isSaving: boolean;
  onDraftChange: (draft: CrmBookingRuleOverrideDraft) => void;
  onNew: () => void;
  onSave: () => void;
  onEdit: (override: CrmBookingRuleOverride) => void;
  onDelete: (overrideId: string) => void;
}

export function createCrmBookingRuleOverrideDraft(
  override?: CrmBookingRuleOverride,
): CrmBookingRuleOverrideDraft {
  return {
    id: override?.id ?? null,
    serviceId: override?.serviceId ?? "",
    customLateCancelHours: override?.customLateCancelHours === null || override?.customLateCancelHours === undefined
      ? ""
      : String(override.customLateCancelHours),
    emergencyPassesRemaining: override?.emergencyPassesRemaining === null || override?.emergencyPassesRemaining === undefined
      ? ""
      : String(override.emergencyPassesRemaining),
    isVip: override?.isVip ?? false,
    notes: override?.notes ?? "",
  };
}

export function CrmContactBookingOverridesCard({
  overrides,
  services,
  draft,
  error,
  isSaving,
  onDraftChange,
  onNew,
  onSave,
  onEdit,
  onDelete,
}: CrmContactBookingOverridesCardProps) {
  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">Booking overrides</p>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Set VIP handling, emergency passes, and custom late-cancel deadlines for this contact.
          </p>
        </div>
        <button
          type="button"
          onClick={() => { onNew(); }}
          className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-xs font-medium text-[var(--color-foreground)] hover:bg-[var(--color-muted)]"
        >
          New override
        </button>
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <label className="space-y-1 text-sm">
          <span className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">Service scope</span>
          <select
            value={draft.serviceId}
            onChange={(event) => onDraftChange({ ...draft, serviceId: event.target.value })}
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          >
            <option value="">All services</option>
            {services.map((service) => (
              <option key={service.id} value={service.id}>{service.name}</option>
            ))}
          </select>
        </label>
        <label className="space-y-1 text-sm">
          <span className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">Custom late-cancel hours</span>
          <input
            type="number"
            min={0}
            value={draft.customLateCancelHours}
            onChange={(event) => onDraftChange({ ...draft, customLateCancelHours: event.target.value })}
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          />
        </label>
        <label className="space-y-1 text-sm">
          <span className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">Emergency passes remaining</span>
          <input
            type="number"
            min={0}
            value={draft.emergencyPassesRemaining}
            onChange={(event) => onDraftChange({ ...draft, emergencyPassesRemaining: event.target.value })}
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          />
        </label>
        <label className="flex items-center gap-2 pt-6 text-sm text-[var(--color-foreground)]">
          <input
            type="checkbox"
            checked={draft.isVip}
            onChange={(event) => onDraftChange({ ...draft, isVip: event.target.checked })}
          />
          Mark as VIP for booking policy
        </label>
        <label className="space-y-1 text-sm md:col-span-2">
          <span className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">Notes</span>
          <textarea
            value={draft.notes}
            onChange={(event) => onDraftChange({ ...draft, notes: event.target.value })}
            rows={2}
            className="w-full resize-none rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          />
        </label>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => { onSave(); }}
          disabled={isSaving}
          className="rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-[var(--color-primary-foreground)] disabled:opacity-60"
        >
          {isSaving ? "Saving..." : draft.id ? "Save override" : "Create override"}
        </button>
        {error ? <p className="text-xs text-[var(--color-destructive)]">{error}</p> : null}
      </div>
      <div className="mt-4 space-y-2">
        {overrides.map((override) => (
          <div key={override.id} className="flex flex-col gap-2 rounded-lg border border-[var(--color-border)] p-3 md:flex-row md:items-center md:justify-between">
            <div className="text-sm text-[var(--color-foreground)]">
              <p className="font-medium">
                {services.find((service) => service.id === override.serviceId)?.name ?? "All services"}
                {override.isVip ? " \u00b7 VIP" : ""}
              </p>
              <p className="text-xs text-[var(--color-muted-foreground)]">
                Late cancel: {override.customLateCancelHours ?? "default"}h{" \u00b7 "}Emergency passes: {override.emergencyPassesRemaining ?? "default"}
              </p>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => { onEdit(override); }} className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-xs hover:bg-[var(--color-muted)]">Edit</button>
              <button type="button" onClick={() => { onDelete(override.id); }} className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-xs hover:bg-[var(--color-muted)]">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
